import { isPerformanceDebugEnabled } from "@/utils/measurement/feature-flag"
import { store } from "../store"
import { uuid } from "@/utils/uuid"
import { atom } from "jotai"

export type TaskPerformanceMeasurementName =
	| "import-metadata"
	| "culling-group"
	| "culling-score"
	| "ai-style-apply"
	| "export"
	| "publish"
export type TaskPerformanceMeasurementStatus = "not-started" | "in-progress" | "done"

export interface TaskPerformanceMeasurement {
	taskTimes: number[]
	startTime: number
	endTime: number
}

interface PendingTaskPerformanceMeasurement {
	name: TaskPerformanceMeasurementName
	time: number
}

const pendingTaskPerformanceMeasurements: Record<string, PendingTaskPerformanceMeasurement> = {}

const createEmptyTaskPerformanceMeasurement = () => {
	return {
		taskTimes: [],
		startTime: 0,
		endTime: 0,
	}
}

export const taskPerformanceMeasurements = atom<
	Record<TaskPerformanceMeasurementName, TaskPerformanceMeasurement>
>({
	"import-metadata": createEmptyTaskPerformanceMeasurement(),
	"culling-group": createEmptyTaskPerformanceMeasurement(),
	"culling-score": createEmptyTaskPerformanceMeasurement(),
	"ai-style-apply": createEmptyTaskPerformanceMeasurement(),
	export: createEmptyTaskPerformanceMeasurement(),
	publish: createEmptyTaskPerformanceMeasurement(),
})

export const beginTaskPerformanceMeasurement = (name: TaskPerformanceMeasurementName) => {
	const measurements = store.get(taskPerformanceMeasurements)
	const measurement = measurements[name]

	if (measurement.endTime !== 0) {
		// Already measured
		return
	}

	measurement.startTime = performance.now()

	store.set(taskPerformanceMeasurements, {
		...measurements,
		[name]: measurement,
	})
}

export const commitTaskPerformanceMeasurement = (name: TaskPerformanceMeasurementName) => {
	const measurements = store.get(taskPerformanceMeasurements)
	const measurement = measurements[name]

	if (measurement.endTime !== 0) {
		// Already measured
		return
	}

	measurement.endTime = performance.now()

	store.set(taskPerformanceMeasurements, {
		...measurements,
		[name]: measurement,
	})
}

export const beginTaskSingleItemPerformanceMeasurement = (name: TaskPerformanceMeasurementName) => {
	if (!isPerformanceDebugEnabled()) {
		return null
	}

	const measurements = store.get(taskPerformanceMeasurements)
	const measurement = measurements[name]

	if (measurement.endTime !== 0) {
		// Already measured
		return null
	}

	const id = uuid()
	pendingTaskPerformanceMeasurements[id] = { name, time: performance.now() }
	return id
}

export const commitTaskSingleItemPerformanceMeasurement = (id: string | null) => {
	if (!isPerformanceDebugEnabled() || !id) {
		return
	}

	const pendingTaskPerformanceMeasurement = pendingTaskPerformanceMeasurements[id]
	if (!pendingTaskPerformanceMeasurement) {
		return
	}

	const { name, time } = pendingTaskPerformanceMeasurement
	delete pendingTaskPerformanceMeasurements[id]

	const taskTime = performance.now() - time

	const measurements = store.get(taskPerformanceMeasurements)
	const measurement = measurements[name]

	measurement.taskTimes = [...measurement.taskTimes, taskTime]

	store.set(taskPerformanceMeasurements, {
		...measurements,
		[name]: measurement,
	})
}

export const getTaskPerformanceMeasurementStatus = (
	name: TaskPerformanceMeasurementName,
): TaskPerformanceMeasurementStatus => {
	const measurements = store.get(taskPerformanceMeasurements)
	const measurement = measurements[name]

	if (measurement.startTime === 0) {
		return "not-started"
	}

	if (measurement.endTime === 0) {
		return "in-progress"
	}

	return "done"
}

export const resetTaskPerformanceMeasurements = () => {
	store.set(taskPerformanceMeasurements, {
		"import-metadata": createEmptyTaskPerformanceMeasurement(),
		"culling-group": createEmptyTaskPerformanceMeasurement(),
		"culling-score": createEmptyTaskPerformanceMeasurement(),
		"ai-style-apply": createEmptyTaskPerformanceMeasurement(),
		export: createEmptyTaskPerformanceMeasurement(),
		publish: createEmptyTaskPerformanceMeasurement(),
	})
}
